class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    window.addEventListener("refresh-cart-drawer", () => {
      this.getCartData();
    });
  }

  updateCartCount(cartCount) {
    if (cartCount == null) return;
    const elements = document.querySelectorAll("#cart-items-count, .cart-count-bubble span");
    if (!elements) return;
    elements.forEach((element) => {
      if (!element) return;
      element.textContent = cartCount;
    });
  }

  async getItemCount() {
    try {
      const res = await fetch("/cart.js");
      if (!res.ok) {
        throw new Error(`Cart API Error: ${res.status}`);
      }
      const cart = await res.json();
      return cart?.item_count || 0;
    } catch (err) {
      console.error("Error fetching cart:", err);
      captureErrorWithScope(err, {
        area: "cart",
        method: "getItemCount",
      });
      return 0;
    }
  }

  async getCartData() {
    try {
      const itemsCount = await this.getItemCount();
      const hasItems = itemsCount > 0;
      const res = await fetch("/?sections=cart-drawer,header");
      const data = await res.json();
      this.renderContents({
        id: null,
        sections: data,
        hasItems,
      });
      this.updateCartCount(itemsCount);
      const drawer = document.querySelector("cart-drawer");
      if (drawer) {
        drawer.classList.remove("is-empty");
      }
    } catch (err) {
      console.error("Cart fetch failed:", err);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll("#cart-drawer-button");
    if (!cartLinks) return;

    cartLinks.forEach((link) => {
      link.setAttribute("role", "button");
      link.setAttribute("aria-haspopup", "dialog");
      link.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(link);
      });
      link.addEventListener("keydown", (event) => {
        if (event.code.toUpperCase() === "SPACE") {
          event.preventDefault();
          this.open(link);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (parsedState?.sections) {
        if (!sectionElement || !parsedState.sections[section.id]) return; //skip to the next element
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    const drawer = document.querySelector("cart-drawer");
    if (parsedState.hasItems && drawer?.classList.contains("is-empty")) {
      drawer.classList.remove("is-empty");
    }

    requestAnimationFrame(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
      updateLoginLinks();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "header",
        selector: "#cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

document.addEventListener("change", async function (event) {
  const target = event.target;
  if (!target.matches(".fp-cart__quantity-selector")) return;
  event.preventDefault();
  const quantity = parseInt(target.value, 10);
  const variantId = target.dataset.variantId;
  try {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
      }),
    });
    if (!res.ok) {
      throw new Error(`Cart API Error: ${res.status}`);
    }
    const updatedCart = await res.json();
  } catch (err) {
    console.error("Failed to update cart quantity:", err);
    captureErrorWithScope(err, {
      area: "cart",
      method: "getItemCount",
    });
  }
});

if (typeof CartItems === "undefined") {
  class CartItems extends HTMLElement {
    getSectionsToRender() {
      return [];
    }
  }
  window.CartItems = CartItems;
}
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "fp-header-icons",
        section: document.getElementById("main-header")?.dataset.id,
        selector: "#cart-icon-bubble",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
